// src/contexts/AuthContext.js
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../utils/axios';
import { normalizeRole } from '../utils/roles';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function ConfigErrorBanner() {
  return (
    <div
      role="alert"
      style={{
        background: '#b91c1c',
        color: '#fff',
        padding: '12px 16px',
        textAlign: 'center',
        fontWeight: 600,
      }}
    >
      Configuración incompleta: faltan REACT_APP_SUPABASE_URL y/o REACT_APP_SUPABASE_ANON_KEY.
      El inicio de sesión no funcionará hasta configurar las variables de entorno.
    </div>
  );
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  const fetchProfile = async (userId, sessionEmail = null) => {
    setIsProfileLoading(true);
    const applyProfile = (data) => {
      if (!data) return;
      const email = data.email ?? sessionEmail ?? null;
      const nombreTrimmed = data.nombre != null ? String(data.nombre).trim() : '';
      const nombre = nombreTrimmed || email || '';
      const role = normalizeRole(data.role);
      setProfile({ ...data, nombre, role });
    };

    try {
      try {
        const { data } = await api.get(`/api/users/${userId}`);
        const user = data?.user || data;
        if (user?.id) {
          applyProfile(user);
          return;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Fetch profile via API failed, falling back to Supabase:', err?.message || err);
        }
      }

      if (!supabase) return;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Fetch profile error:', error);
        }
        return;
      }
      applyProfile(data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Fetch profile error:', err);
      }
    } finally {
      setIsProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const failSafe = setTimeout(() => {
      if (!cancelled) {
        setIsLoading((still) => {
          if (still) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn(
                'Auth: initialization took too long; leaving loading state. Check Supabase and network.'
              );
            }
            return false;
          }
          return still;
        });
      }
    }, 15000);

    const applySession = async (session) => {
      if (cancelled) return;
      setUser(session?.user ?? null);
      if (session?.user) {
        setIsLoading(false);
        void fetchProfile(session.user.id, session.user.email ?? null);
      } else {
        setProfile(null);
        setIsLoading(false);
      }
    };

    void (async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('getSession error:', error);
          }
          setIsLoading(false);
          return;
        }
        await applySession(session ?? null);
      } catch (e) {
        if (!cancelled) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Auth getSession:', e);
          }
          setIsLoading(false);
        }
      }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (cancelled) return;
        if (event === 'INITIAL_SESSION' && !session) {
          try {
            const { data: { session: recovered }, error } = await supabase.auth.getSession();
            if (cancelled) return;
            if (error) {
              if (process.env.NODE_ENV !== 'production') {
                console.warn('Session recovery error:', error);
              }
              await applySession(null);
              return;
            }
            await applySession(recovered ?? null);
          } catch (err) {
            if (!cancelled) {
              if (process.env.NODE_ENV !== 'production') {
                console.warn('Session recovery error:', err);
              }
              await applySession(null);
            }
          }
          return;
        }
        await applySession(session);
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(failSafe);
      subscription?.unsubscribe?.();
    };
  }, []);

  const login = async ({ email, password }) => {
    if (!supabase) {
      return { success: false, error: 'Auth not configured — missing Supabase environment variables' };
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { success: !error, error: error?.message };
  };

  const register = async ({ email, password, nombre }) => {
    if (!supabase) {
      return { success: false, error: 'Auth not configured — missing Supabase environment variables' };
    }
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { nombre } }
    });
    return { success: !error, error: error?.message };
  };

  const logout = async () => {
    setUser(null);
    setProfile(null);
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Logout error:', err);
        }
      }
    }
  };

  const resetPassword = async (email) => {
    if (!supabase) {
      return { success: false, error: 'Auth not configured — missing Supabase environment variables' };
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    return { success: !error, error: error?.message };
  };

  const displayName = useMemo(() => {
    const n = profile?.nombre != null ? String(profile.nombre).trim() : '';
    return n || profile?.email || user?.email || '';
  }, [profile, user]);

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      displayName,
      isAuthenticated: !!user,
      isLoading,
      isProfileLoading,
      isSupabaseConfigured,
      login,
      register,
      logout,
      resetPassword,
      supabase
    }}>
      {!isSupabaseConfigured ? <ConfigErrorBanner /> : null}
      {children}
    </AuthContext.Provider>
  );
};
